import { QrGalleryGrid } from '@/components/demo/QrGalleryGrid';
import { Button } from '@/components/ui/button';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { getDemoQrGallery, isDemoModeEnabled } from '@/lib/demo';
import { useAuthStore } from '@/stores/auth-store';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { Printer } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_app/admin/demo_/qr-gallery')({
  beforeLoad: () => {
    if (!isDemoModeEnabled()) {
      throw notFound();
    }
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden ver la galería demo.');
      throw redirect({ to: '/' });
    }
  },
  component: DemoQrGalleryPage,
});

const PAGE_SIZE = 30;

function DemoQrGalleryPage() {
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['demo', 'qr-gallery', page, PAGE_SIZE],
    queryFn: () => getDemoQrGallery(page, PAGE_SIZE),
    staleTime: 30_000,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <main className="flex-1 p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 no-print">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Galería de QRs demo</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {data ? `${data.total} inscritos` : 'Cargando…'}. Actores primero, luego seed.
          </p>
        </div>
        <Button onClick={() => window.print()} variant="outline">
          <Printer className="mr-2 h-4 w-4" />
          Imprimir
        </Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no identity
            <Skeleton key={i} className="h-72 w-full" />
          ))}
        </div>
      ) : isError || !data ? (
        <p className="text-destructive text-sm">No se pudo cargar la galería.</p>
      ) : data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay inscritos en el evento demo todavía.</p>
      ) : (
        <QrGalleryGrid items={data.items} />
      )}

      {data && totalPages > 1 && (
        <div className="no-print">
          <Pagination className="w-auto mx-0">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={page > 1 ? () => setPage(page - 1) : undefined}
                  aria-disabled={page <= 1}
                  className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              <PaginationItem>
                <span className="px-3 py-1.5 text-sm">
                  {page} / {totalPages}
                </span>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  onClick={page < totalPages ? () => setPage(page + 1) : undefined}
                  aria-disabled={page >= totalPages}
                  className={
                    page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </main>
  );
}
